import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ProviderDataService from '../../services/provider.service';
import { useTranslation } from '../../hooks/useTranslation';

const ProviderForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    is_active: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  useEffect(() => {
    if (isEdit) {
      fetchProvider();
    }
  }, [id, isEdit]);

  const fetchProvider = async () => {
    setLoading(true);
    try {
      const response = await ProviderDataService.get(id);
      const provider = response.data?.data;
      setFormData({
        name: provider.name || '',
        email: provider.email || '',
        phone: provider.phone || '',
        address: provider.address || '',
        is_active: provider.is_active !== undefined ? provider.is_active : true,
      });
    } catch (error) {
      console.error('Error loading provider:', error);
      showSnackbar(t('providers.errorLoadingProvider'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showSnackbar(t('providers.pleaseFillProviderName'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await ProviderDataService.update(id, formData);
        showSnackbar(t('providers.providerUpdatedSuccess'));
      } else {
        await ProviderDataService.create(formData);
        showSnackbar(t('providers.providerCreatedSuccess'));
      }
      navigate('/providers');
    } catch (error) {
      showSnackbar(`Error ${isEdit ? 'updating' : 'creating'} provider: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/providers')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('providers.editProvider') : t('providers.newProvider')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('common.update') : t('common.save')} {t('providers.title')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('providers.name')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
              }
              label={t('providers.status')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('providers.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('providers.phone')}
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('providers.address')}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ProviderForm;

